import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { isWhiteLabel } from "../services/agencyService";
import { useSelector } from "react-redux";
import { selectUserId } from "../store/slices/authUserSlice";
import debounce from "lodash.debounce";
import onboardingService from "../services/onboardingService";
import campaignService from "../services/campaignService";
import { NewsletterEdition } from "../types/newsletterEdition";
import { ReactComponent as DraggableDotsIcon } from "../assets/icons/draggableDots.svg";
import { ReactComponent as EditIcon } from "../assets/icons/edit.svg";
import { ReactComponent as TrashIcon } from "../assets/icons/trash.svg";
import { BlueprintSection } from "./Onboarding/Types";
import SidePanel from "./SidePanel";
import BlueprintSectionPanel from "./BlueprintSectionPanel";
import BlueprintSettingsPanel from "./Onboarding/BlueprintSettingsPanel";
import InstantSampleLoadingModal from "./InstantSampleLoadingModal";
import { getPrimaryBlueprint, setPrimaryBlueprint } from "../services/organizationService";
import { BLUEPRINT_SECTION_EXAMPLES, SECTION_FORMAT_TO_DISPLAY_NAME } from "./Onboarding/Constants";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";


const Blueprint: React.FC = () => {
  const [sections, setSections] = useState<BlueprintSection[]>([]);
  const [isSidePanelOpen, setIsSidePanelOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cadence, setCadence] = useState("");
  const [prepCadence, setPrepCadence] = useState<{ frequency?: string; day_of_week?: string; start_date?: string } | null>(null);
  const [sendCadence, setSendCadence] = useState<{ frequency?: string; day_of_week?: string; start_date?: string } | null>(null);
  const [audience, setAudience] = useState("");
  const [additionalDetails, setAdditionalDetails] = useState("");
  const [operatorNotes, setOperatorNotes] = useState("");
  const [firstPersonVoice, setFirstPersonVoice] = useState("");
  const [isSettingsPanelOpen, setIsSettingsPanelOpen] = useState(false);
  const [isLoadingWelcomeEmail, setIsLoadingWelcomeEmail] = useState(false);
  const [isLoadingInstantSample, setIsLoadingInstantSample] = useState(false);
  const [title, setTitle] = useState("");
  const [referenceEditionId, setReferenceEditionId] = useState<string | null>(null);
  const [referenceEditionHtml, setReferenceEditionHtml] = useState<string | null>(null);
  const [sourceTemplateId, setSourceTemplateId] = useState<number | null>(null);
  const [availableEditions, setAvailableEditions] = useState<{ id: string; title: string }[]>([]);
  const [activeTab, setActiveTab] = useState<"sections" | "template">(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("tab") === "template" ? "template" : "sections";
  });

  const [isPrimary, setIsPrimary] = useState(false);
  const [togglingPrimary, setTogglingPrimary] = useState(false);
  const [nextReviewDate, setNextReviewDate] = useState<string | null>(null);
  const [nextSendDate, setNextSendDate] = useState<string | null>(null);
  const [nextScheduledEditionDate, setNextScheduledEditionDate] = useState<string | null>(null);
  const [autoApprove, setAutoApprove] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();
  const { blueprintId } = useParams<{ blueprintId: string }>();
  const isOnboarding = location.pathname.includes('onboarding');

  const userId = useSelector(selectUserId);

  const fetchBlueprint = async (blueprintId: string) => {
    return onboardingService.getOnboardingBlueprintById(blueprintId);
  };

  useEffect(() => {
    campaignService.listNewsletterEditions().then((editions: NewsletterEdition[]) => {
      setAvailableEditions(editions.map(e => ({ id: e.id, title: e.title })));
    }).catch(err => console.error("Error loading editions:", err));
  }, []);

  useEffect(() => {
    if (!blueprintId) return;
    fetchBlueprint(blueprintId).then(blueprint => {
      setSections(blueprint?.raw_info || []);
      setCadence(blueprint?.cadence || "");
      setPrepCadence(blueprint?.potions_team_delivery_cadence || null);
      setSendCadence(blueprint?.default_sending_cadence || null);
      setAudience(blueprint?.audience || "");
      setAdditionalDetails(blueprint?.additional_details || "");
      setOperatorNotes(blueprint?.operator_notes || "");
      setFirstPersonVoice(blueprint?.first_person_voice || "");
      setTitle(blueprint?.title || "Untitled Blueprint");
      setReferenceEditionId(blueprint?.reference_edition_id || null);
      setReferenceEditionHtml(blueprint?.reference_edition_html || null);
      setSourceTemplateId(blueprint?.source_template_id || null);
      setNextReviewDate(blueprint?.next_review_date || null);
      setNextSendDate(blueprint?.next_send_date || null);
      setNextScheduledEditionDate(blueprint?.next_scheduled_edition_date || null);
      setAutoApprove(blueprint?.auto_approve || false);
    });
  }, [blueprintId]);

  useEffect(() => {
    if (!blueprintId) return;
    getPrimaryBlueprint().then((data) => {
      setIsPrimary(data.id === parseInt(blueprintId));
    }).catch(() => {});
  }, [blueprintId]);

  const handleTogglePrimary = async () => {
    if (!blueprintId) return;
    setTogglingPrimary(true);
    try {
      if (isPrimary) {
        await setPrimaryBlueprint(null);
        setIsPrimary(false);
      } else {
        await setPrimaryBlueprint(parseInt(blueprintId));
        setIsPrimary(true);
      }
    } catch (err) {
      console.error("Failed to toggle primary blueprint:", err);
    } finally {
      setTogglingPrimary(false);
    }
  };

  const saveBlueprintSections = async (newSections: BlueprintSection[]) => {
    if (!blueprintId) return;
    return onboardingService.updateOnboardingBlueprint({
      blueprintId,
      title,
      sections: newSections,
      audience,
      additionalDetails,
      operatorNotes,
      firstPersonVoice,
      sourceTemplateId,
    });
  };

  const saveBlueprintTitle = async (newTitle: string) => {
    if (!blueprintId) return;

    return onboardingService.updateOnboardingBlueprint({
      blueprintId,
      title: newTitle,
      sections,
      audience,
      additionalDetails,
      operatorNotes,
      firstPersonVoice,
      sourceTemplateId,
    });
  };

  const debouncedSaveTitle = useCallback(
    debounce((newTitle: string) => {
      if (!blueprintId) return;

      saveBlueprintTitle(newTitle).catch(error => {
        console.error("Error saving blueprint title:", error);
      });
    }, 500),
    // dependencies to ensure the debounced function has access to current values
    [blueprintId, sections, audience, additionalDetails, operatorNotes, firstPersonVoice]
  );

  const handleTitleChange = (newTitle: string) => {
    setTitle(newTitle);
    debouncedSaveTitle(newTitle);
  };

  const saveBlueprintSettings = async (newAudience: string, newAdditionalDetails: string, newOperatorNotes: string, newFirstPersonVoice: string) => {
    setAudience(newAudience);
    setAdditionalDetails(newAdditionalDetails);
    setOperatorNotes(newOperatorNotes);
    setFirstPersonVoice(newFirstPersonVoice);

    try {
      if (!blueprintId) return;
      const response = await onboardingService.updateOnboardingBlueprint({
        blueprintId,
        title,
        sections,
        audience: newAudience,
        additionalDetails: newAdditionalDetails,
        operatorNotes: newOperatorNotes,
        firstPersonVoice: newFirstPersonVoice,
        sourceTemplateId,
      });
      return response;
    } catch (error) {
      console.error("Error saving blueprint settings:", error);
      throw error;
    }
  };

  const handleGenerateWelcome = async () => {
    if (!userId || !blueprintId) {
      console.error("userId or blueprintId not found. Cannot generate welcome email.");
      return;
    }

    setIsLoadingWelcomeEmail(true);

    let beefreeTemplate;
    try {
      beefreeTemplate = await onboardingService.getBeefreeWelcomeEmail(userId, blueprintId);
    } catch (error) {
      console.error("Error generating welcome email:", error);
      setIsLoadingWelcomeEmail(false);
      return; // stop execution if fetching the template fails
    }

    try {
      const today = new Date();
      const arrivalDate = new Date(today);
      arrivalDate.setDate(today.getDate() + 1);
      const distributionDate = new Date(today);
      distributionDate.setDate(today.getDate() + 4);

      const welcomeEmailData = {
        title: "Welcome to My Newsletter!",
        email_template: beefreeTemplate,
        newsletter_timestamp: arrivalDate.toISOString(),
        scheduled_send_start_date: distributionDate.toISOString(),
      };

      const createdEditionId: string = await campaignService.createNewsletterEdition(welcomeEmailData);
      setIsLoadingWelcomeEmail(false);
      navigate(`/newsletterEdition/${createdEditionId}`);
    } catch (error) {
      setIsLoadingWelcomeEmail(false);
      console.error("Error generating welcome email:", error);
    }
  };

  const handleGenerateSample = async () => {
    if (!userId || !blueprintId) {
      console.error("userId or blueprintId not found. Cannot generate Beefree sample.");
      return;
    }

    setIsLoadingInstantSample(true);

    let beefreeTemplate;
    try {
      const start = performance.now();

      beefreeTemplate = await onboardingService.getBeefreeInstantSample(userId, blueprintId);

      const end = performance.now();
      const duration = (end - start) / 1000;
      console.log(`Instant sample generation took ${duration.toFixed(2)} seconds`);
    } catch (error) {
      console.error("Error generating instant sample:", error);
      setIsLoadingInstantSample(false);
      return; // stop execution if fetching the template fails
    }

    try {
      const today = new Date();
      const arrivalDate = new Date(today);
      arrivalDate.setDate(today.getDate() + 1);
      const distributionDate = new Date(today);
      distributionDate.setDate(today.getDate() + 4);

      const instantSampleData = {
        title: "Instant Sample",
        email_template: beefreeTemplate,
        newsletter_timestamp: arrivalDate.toISOString(),
        scheduled_send_start_date: distributionDate.toISOString(),
        onboarding_blueprint_id: blueprintId,
      };
      const createdEditionId: string = await campaignService.createNewsletterEdition(instantSampleData);
      setIsLoadingInstantSample(false);
      navigate(`/newsletterEdition/${createdEditionId}`);
    } catch (error) {
      setIsLoadingInstantSample(false);
      console.error("Error creating instant sample with beefree template:", error);
    }
  };

  const addSection = () => {
    const contentType = Object.keys(BLUEPRINT_SECTION_EXAMPLES)[0];
    const format = Object.keys(BLUEPRINT_SECTION_EXAMPLES[contentType])[0];
    const newSection: BlueprintSection = {
      title: 'New Section',
      content_type: contentType,
      format: format,
    };

    const newSections = [...sections, newSection];

    // add the new section, then open the side panel for it
    saveBlueprintSections(newSections).then(blueprint => {
      setSections(blueprint.raw_info);
      setActiveIndex(newSections.length - 1);
      setIsSidePanelOpen(true);
      setIsSettingsPanelOpen(false);
    });
  };

  const updateSection = (index: number, updatedSection: Partial<BlueprintSection>) => {
    const oldSections = sections;
    const newSections = oldSections.map((section: BlueprintSection, i: number) =>
      i === index ? { ...section, ...updatedSection } : section
    );

    saveBlueprintSections(newSections).then(blueprint => {
      setSections(blueprint.raw_info);
    });
  };

  const deleteSection = (indexToRemove: number) => {
    const newSections = [
      ...sections.slice(0, indexToRemove),
      ...sections.slice(indexToRemove + 1),
    ];

    saveBlueprintSections(newSections).then(blueprint => {
      setSections(blueprint.raw_info);
    });
  };

  const handleDragEnd = (result: any) => {
    const { source, destination } = result;

    // return if outside if outside area or same position
    if (!destination) return;
    if (destination.index === source.index) return;

    const reorderedSections = Array.from(sections);
    const [movedItem] = reorderedSections.splice(source.index, 1);
    reorderedSections.splice(destination.index, 0, movedItem);

    setSections(reorderedSections);
    saveBlueprintSections(reorderedSections).catch(error => {
      console.error("Error saving reordered sections:", error);
    });
  };

  const openSettingsPanel = () => {
    setIsSettingsPanelOpen(true);
    setIsSidePanelOpen(false);
  };

  const [pendingSampleEditionId, setPendingSampleEditionId] = useState<string | null>(null);

  const currentSampleEditionId = referenceEditionId ?? "";

  const handleSampleEditionChange = (value: string) => {
    setPendingSampleEditionId(value);
  };

  const saveSampleEdition = async () => {
    if (!blueprintId || pendingSampleEditionId === null) return;
    const newEditionId = pendingSampleEditionId || null;
    setReferenceEditionId(newEditionId);
    setPendingSampleEditionId(null);
    try {
      await onboardingService.updateOnboardingBlueprint({
        blueprintId,
        title,
        sections,
        audience,
        additionalDetails,
        operatorNotes,
        sourceTemplateId,
        referenceEditionId: newEditionId,
      });
      const updated = await fetchBlueprint(blueprintId);
      let newHtml = updated?.reference_edition_html || null;
      if (!newHtml && newEditionId) {
        newHtml = await campaignService.renderEditionHtml(newEditionId);
      }
      setReferenceEditionHtml(newHtml);
      if (newHtml) setActiveTab("template");
    } catch (error) {
      console.error("Error saving sample edition:", error);
    }
  };

  return (
    <div
      onClick={() => {
        if (isSidePanelOpen) {
          setIsSidePanelOpen(false);
        }
        if (isSettingsPanelOpen) {
          setIsSettingsPanelOpen(false);
        }
      }}
    >
      <div
        style={{
          filter: isLoadingInstantSample || isLoadingWelcomeEmail ? "blur(4px)" : "none",
          pointerEvents: isLoadingInstantSample || isLoadingWelcomeEmail ? "none" : "auto",
        }}
      >
        <div style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          width: "100%",
          minWidth: "880px",
        }}>
          <h1 style={{ minWidth: "50%" }}>
            <input
              type="text"
              value={title}
              onChange={(e) => handleTitleChange(e.target.value)}
              style={{
                fontSize: "1em",
                fontWeight: "bold",
                border: "none",
                outline: "none",
                width: "100%",
                backgroundColor: "transparent",
                padding: "0",
                cursor: "text",
              }}
            />
          </h1>

          <button
            onClick={handleTogglePrimary}
            disabled={togglingPrimary}
            style={{
              padding: "4px 12px", borderRadius: "6px",
              border: isPrimary ? "1px solid #6366F1" : "1px solid #D1D5DB",
              backgroundColor: isPrimary ? "#EEF2FF" : "#FFFFFF",
              fontSize: "12px", fontWeight: 500,
              color: isPrimary ? "#6366F1" : "#6B7280",
              cursor: togglingPrimary ? "wait" : "pointer",
              whiteSpace: "nowrap", marginLeft: "12px",
            }}
          >
            {isPrimary ? "Primary Blueprint" : "Set as Primary"}
          </button>

          <div style={{ display: "inline-block", marginLeft: "auto" }}>
            <button
              className="tertiary-info-button"
              style={{
                cursor: "pointer",
                marginRight: "12px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                navigate(`/blueprints/import-strategy?blueprintId=${blueprintId}`);
              }}
            >
              Import Strategy Doc
            </button>

            <button
              className="tertiary-info-button"
              style={{
                cursor: "pointer",
                marginRight: "12px",
              }}
              onClick={(e) => {
                e.stopPropagation();
                openSettingsPanel();
              }}
            >
              Prompts
            </button>

            <button
              className="tertiary-info-button"
              style={{
                cursor: "pointer",
                marginRight: "12px",
              }}
              onClick={(e) => {
                e.stopPropagation(); // short-circuit the parent's `onClick` behavior

                addSection();
              }}
            >
              Add section
            </button>

          </div>
        </div>

        {(prepCadence?.frequency || cadence || nextReviewDate || nextSendDate || nextScheduledEditionDate) && (() => {
          const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);
          const NEXT_DAY: Record<string, string> = {
            monday: "Tuesday", tuesday: "Wednesday", wednesday: "Thursday",
            thursday: "Friday", friday: "Saturday", saturday: "Sunday", sunday: "Monday",
          };

          const frequency = prepCadence?.frequency;
          const prepDay = prepCadence?.day_of_week;
          const preparedDay = prepDay ? NEXT_DAY[prepDay] : null;
          const sendDay = sendCadence?.day_of_week;

          const freqLabel = frequency ? capitalize(frequency) : null;

          const tooltipText = [
            preparedDay && `${isWhiteLabel() ? "We" : "Potions"} will prepare your newsletter and send it for your review on ${preparedDay}s.`,
            sendDay && `We aim to send it to your subscribers on ${capitalize(sendDay)}s.`,
            "Days may shift by a day if the newsletter needs revisions after internal review.",
          ].filter(Boolean).join(" ");

          const InfoIcon = () => (
            <span title={tooltipText} style={{ cursor: "help", display: "inline-flex", alignItems: "center", marginLeft: "6px", verticalAlign: "middle" }}>
              <svg width="16" height="16" viewBox="0 0 16 16" fill="none" style={{ display: "block" }}>
                <circle cx="8" cy="8" r="7" stroke="#9CA3AF" strokeWidth="1.5" fill="none" />
                <text x="8" y="11.5" textAnchor="middle" fontSize="10" fontWeight="600" fill="#9CA3AF">i</text>
              </svg>
            </span>
          );

          return (
            <div style={{
              marginTop: "0", marginBottom: "20px", padding: "12px 16px",
              backgroundColor: "#F9FAFB", borderRadius: "8px", border: "1px solid #E5E7EB",
              fontSize: "14px", color: "#374151", lineHeight: 1.6,
            }}>
              {freqLabel && (
                <span style={{ fontWeight: 600 }}>{freqLabel}</span>
              )}
              {preparedDay && (
                <span> &middot; Prepared {preparedDay}s</span>
              )}
              {sendDay && (
                <span> &middot; Sent {capitalize(sendDay)}s</span>
              )}
              {!freqLabel && cadence && <span>{cadence}</span>}
              {(preparedDay || sendDay) && <InfoIcon />}
              {nextReviewDate && (
                <span style={{ marginLeft: "16px", color: "#6B7280" }}>
                  Ready for your review on <strong>{new Date(nextReviewDate).toLocaleDateString()}</strong>
                </span>
              )}
              {(nextScheduledEditionDate || nextSendDate) && (
                <span style={{ marginLeft: "16px", color: "#6B7280" }}>
                  Target send date: <strong>{new Date((nextScheduledEditionDate || nextSendDate)!).toLocaleDateString()}</strong>
                </span>
              )}
              <span style={{ marginLeft: "16px" }}>
                {autoApprove
                  ? <span style={{ color: "#059669", fontWeight: 600 }}>Auto-approve</span>
                  : <span style={{ color: "#6B7280" }}>Manual approval</span>
                }
              </span>
            </div>
          );
        })()}

        {availableEditions.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
              <label style={{ fontSize: "14px", color: "#6b7280", whiteSpace: "nowrap" }}>
                Sample edition:
              </label>
              <select
                value={pendingSampleEditionId ?? currentSampleEditionId}
                onChange={(e) => { e.stopPropagation(); handleSampleEditionChange(e.target.value); }}
                onClick={(e) => e.stopPropagation()}
                style={{
                  fontSize: "14px",
                  padding: "4px 8px",
                  borderRadius: "6px",
                  border: "1px solid #d1d5db",
                  backgroundColor: "white",
                  cursor: "pointer",
                }}
              >
                <option value="">— None —</option>
                {availableEditions.map(e => (
                  <option key={e.id} value={e.id}>{e.title}</option>
                ))}
              </select>
              {pendingSampleEditionId !== null && pendingSampleEditionId !== currentSampleEditionId && (
                <button
                  className="primary-button"
                  style={{ padding: "4px 12px", fontSize: "14px" }}
                  onClick={(e) => { e.stopPropagation(); saveSampleEdition(); }}
                >
                  Save
                </button>
              )}
            </div>
            <p style={{ margin: "4px 0 0 0", fontSize: "13px", color: "#9ca3af" }}>
              When a new edition is created from this blueprint, {isWhiteLabel() ? "we" : "Potions"} will use this edition's layout as the starting template.
            </p>
          </div>
        )}

        <div className="blueprint-tabs">
          <button
            className={`blueprint-tab${activeTab === "sections" ? " active" : ""}`}
            onClick={(e) => { e.stopPropagation(); setActiveTab("sections"); }}
          >
            Sections
          </button>
          {referenceEditionId && (
            <button
              className={`blueprint-tab${activeTab === "template" ? " active" : ""}`}
              onClick={(e) => { e.stopPropagation(); setActiveTab("template"); }}
            >
              Template
            </button>
          )}
        </div>

        {activeTab === "sections" && (
          <>
            <p style={{ marginTop: "12px", marginBottom: "16px", color: "#6b7280", fontSize: "14px" }}>
              These sections are instructions for how {isWhiteLabel() ? "we" : "Potions"} will fill out your template using AI.
              Edit, reorder, or add sections to control what goes into each part of your email.
            </p>
            <div className="blueprint-header">
              <div style={{ marginLeft: "20px" }}>
                <span>Section Name</span>
              </div>

              <div style={{ marginRight: "64px" }}>
                <span className="blueprint-header-right">Content Type</span>
                <span className="blueprint-header-right">Format</span>
              </div>
            </div>

            <DragDropContext onDragEnd={handleDragEnd}>
              <Droppable droppableId="droppable" direction="vertical">

                {(provided: any) => (
                  <ul
                    className="newsletter-list"
                    {...provided.droppableProps}
                    ref={provided.innerRef}
                  >
                    {sections.map((section: BlueprintSection, index) => (
                      <Draggable key={section.title} draggableId={section.title} index={index}>

                        {(provided: any) => (
                          <li
                            className="newsletter-item"
                            ref={provided.innerRef}
                            {...provided.draggableProps}
                            {...provided.dragHandleProps}
                            style={{
                              marginTop: "16px",
                              cursor: "pointer",
                              ...provided.draggableProps.style,
                            }}
                            onClick={(e) => {
                              e.stopPropagation();

                              setActiveIndex(index);
                              setIsSidePanelOpen(true);
                              setIsSettingsPanelOpen(false);
                            }}
                          >
                            <div
                              className="newsletter-item-content-left"
                              style={{ flexDirection: "row", justifyContent: "center" }}
                            >
                              <DraggableDotsIcon
                                aria-label="Draggable"
                                style={{
                                  width: "1.5em",
                                  height: "2em",
                                  marginLeft: "4px",
                                  cursor: "grab",
                                }}
                              />
                              <span
                                className="newsletter-title"
                                style={{
                                  paddingLeft: "8px",
                                }}
                              >
                                {section.title}
                              </span>
                            </div>

                            <div style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "12px",
                            }}>
                              <span className="list-tag-gray">
                                {section.content_type}
                              </span>

                              <span className="list-tag-gray">
                                {SECTION_FORMAT_TO_DISPLAY_NAME[section.format] || section.format}
                              </span>

                              <EditIcon
                                aria-label="Edit"
                                style={{
                                  width: "2em",
                                  height: "2em",
                                  marginRight: "-8px",
                                }}
                              />

                              <TrashIcon
                                aria-label="Delete"
                                style={{
                                  width: "2em",
                                  height: "2em",
                                  marginRight: "12px",
                                  cursor: "pointer",
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();

                                  const confirmed = window.confirm(
                                    "Are you sure you want to delete this section? This action cannot be undone."
                                  );

                                  if (confirmed) {
                                    deleteSection(index);
                                  }
                                }}
                              />
                            </div>
                          </li>
                        )}

                      </Draggable>
                    ))}
                    {provided.placeholder}
                  </ul>
                )}

              </Droppable>
            </DragDropContext>
          </>
        )}

        {activeTab === "template" && referenceEditionId && (
          <div className="blueprint-template-preview">
            <div className="blueprint-template-preview-header">
              <p style={{ margin: 0, color: "#6b7280", fontSize: "13px", flex: 1 }}>
                This is a preview of your template layout with placeholder content. Your actual emails
                will be filled in by {isWhiteLabel() ? "us" : "Potions"} based on your sections and real content.
              </p>
              <button
                className="tertiary-info-button"
                style={{ cursor: "pointer" }}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/newsletterEdition/${referenceEditionId}`);
                }}
              >
                Edit template
              </button>
            </div>
            {referenceEditionHtml && (
              <div className="blueprint-template-preview-frame">
                <iframe
                  srcDoc={referenceEditionHtml}
                  title="Template preview"
                  sandbox="allow-same-origin"
                  className="blueprint-template-preview-iframe"
                />
              </div>
            )}
          </div>
        )}

        {isOnboarding &&
          <div style={{ textAlign: "center", marginTop: "16px", marginBottom: "-16px" }}>
            <p>See how {isWhiteLabel() ? "we" : "Potions"} will fill out these sections for you.</p>
          </div>
        }

        {/* Panels and Modals */}


        <div onClick={(e) => e.stopPropagation()}>
          <SidePanel isOpen={isSidePanelOpen} onClose={() => setIsSidePanelOpen(false)}>
            <BlueprintSectionPanel sections={sections} index={activeIndex} updateSection={updateSection} />
          </SidePanel>
        </div>

        <div onClick={(e) => e.stopPropagation()}>
          <SidePanel isOpen={isSettingsPanelOpen} onClose={() => setIsSettingsPanelOpen(false)}>
            <BlueprintSettingsPanel
              audience={audience}
              additionalDetails={additionalDetails}
              operatorNotes={operatorNotes}
              firstPersonVoice={firstPersonVoice}
              saveBlueprintSettings={saveBlueprintSettings}
            />
          </SidePanel>
        </div>

      </div>

      {isLoadingInstantSample || isLoadingWelcomeEmail && <InstantSampleLoadingModal />}
    </div>
  );
};

export default Blueprint;
